import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  ViewChild,
  AfterViewInit
} from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Navigate } from 'libs/shared/src/lib/states/router/router.actions';
import { PATH_DEFINITIONS } from 'libs/shared/src/lib/definitions/path-definition';
import { ProjectModelWithRelations } from '@rappider/rappider-sdk';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { RappiderButtonModule } from 'libs/components/src/lib/components/button/button.module';
import { Subscription } from 'rxjs';
import { NewProjectModelFieldModule } from '@rappider/project-model-field';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import {
  DataModelDisplayWithFieldsComponent,
  EntityConfigInterface,
  ListMode,
  RappiderCrudViewModule,
  RappiderLottiePlayerModule,
  ViewMode,
  RappiderCrudViewComponent
} from '@rappider/rappider-components';
import { IconType } from 'libs/components/src/lib/utils/icon';
import { getProjectModelsWithFields } from '@rappider/shared';
import { EntityMetabaseService } from '../../services/entity-metabase.service';
import { EntityService } from '../../services/entity.service';
import { LoopbackCrudApiService } from '../../services/loopback-crud-api.service';
import { EntityVisualizationService } from '../../services/entity-visualization.service';
import { FormsModule } from '@angular/forms';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { CompiledComponentRendererComponent } from
  'libs/content-tree-renderer/src/lib/components/compiled-component-renderer/compiled-component-renderer.component';
import { GridBuilderConfig } from '../project-model-entities/project-model-entities.component';
import * as ProjectModelActions from 'libs/project/src/lib/states/project-model-state/project-model.actions';
import { CrudFormConfig } from 'libs/components/src/lib/utils/edit-form/crud-form-config.interface';
import { ButtonType } from 'libs/components/src/lib/utils/button/button-type.enum';
import { ButtonSize } from 'libs/components/src/lib/utils/button/button-size.enum';
import { RappiderAlertModule } from 'libs/components/src/lib/components/alert/alert.module';
import { AlertTypes } from 'libs/components/src/lib/utils/alert/alert-types';
import { HeadingType } from 'libs/components/src/lib/utils/heading';
import { RappiderJsonViewerComponent } from 'libs/components/src/lib/components/json-viewer/json-viewer.component';
import { JsonataParserService } from '@rappider/shared';

@Component({
  selector: 'rappider-manage-ui-entity',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    NzButtonModule,
    NzToolTipModule,
    NzSpinModule,
    NzEmptyModule,
    NzCardModule,
    NzIconModule,
    NzSelectModule,
    RappiderButtonModule,
    NewProjectModelFieldModule,
    NzCollapseModule,
    DataModelDisplayWithFieldsComponent,
    RappiderCrudViewModule,
    NzSkeletonModule,
    RappiderLottiePlayerModule,
    CompiledComponentRendererComponent,
    RappiderAlertModule,
    RappiderJsonViewerComponent
  ],
  templateUrl: './manage-ui-entity.component.html',
  styleUrls: ['./manage-ui-entity.component.scss']
})
export class ManageUiEntityComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {

  @Input() projectModelId: string | undefined;
  @Output() splitterToggleRequested = new EventEmitter<boolean>();

  subscriptions: Subscription[] = [];

  projectModels: ProjectModelWithRelations[] | undefined;
  selectedProjectModel: ProjectModelWithRelations | undefined;

  entityConfig: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  entityData: any[] | undefined; // eslint-disable-line @typescript-eslint/no-explicit-any
  isEntityDataLoading = false;
  isExampleData = false;

  displayedSection = 'all';
  expandAllSections = false;
  isFirstPanelExpanded = false;
  private pendingSection: string | null = null;

  activeEntityConfig: EntityConfigInterface | undefined;
  recentGridConfigUpdate: string | null = null;

  private metadataConfig: any; // eslint-disable-line @typescript-eslint/no-explicit-any

  // Selected item index for detail view
  selectedItemDataViewIndex = 0;

  // ViewChild reference to Item Data View's crud-view component
  @ViewChild('itemDataViewCrudView', { static: false }) itemDataViewCrudView: RappiderCrudViewComponent | undefined;

  // Component caching for performance
  private componentCache = {
    hasDisplayComponent: false,
    bundleUrl: null as string | null,
    definitionId: null as string | null,
    lastProjectModelId: null as string | null,
    isValid: false
  };

  // Cached component definition for template
  get componentDefinition() {
    this.updateComponentCacheIfNeeded();
    return {
      bundleUrl: this.componentCache.bundleUrl,
      id: this.componentCache.definitionId
    };
  }

  // Cached component input for template
  get componentInput() {
    this.updateComponentCacheIfNeeded();
    return {
      id: `entity_component_${this.selectedProjectModel?.id}_${this.componentCache.definitionId}`
    };
  }

  ViewMode = ViewMode;
  ListMode = ListMode;
  ButtonType = ButtonType;
  ButtonSize = ButtonSize;
  IconType = IconType;

  startAnimationConfig = {
    options: {
      autoplay: true,
      loop: true,
      path: 'https://lottie.host/63b3e56a-3b88-4037-be84-2b2fceb88b90/O98MCCVRAX.json'
    },
    height: '200px'
  };


  /**
   * Get transformJsonataConfig from metadata config for JSON viewer
   * Returns the config as-is if it's an object, attempts to parse if string
   * @returns Config object if exists, parsed structure if string, null otherwise
   */
  get transformJsonataConfig(): any { // eslint-disable-line @typescript-eslint/no-explicit-any
    const config = this.metadataConfig?.displayWithComponent?.flutter?.item?.transformJsonataConfig;

    if (!config) {
      return null;
    }

    if (typeof config === 'string') {
      try {
        return JSON.parse(config);
      } catch {
        // If parsing fails (e.g., JSONata expression), use parser service
        return this.jsonataParserService.parseJsonataExpression(config);
      }
    }

    return config;
  }

  constructor(
    private store: Store<unknown>,
    private router: Router,
    private route: ActivatedRoute,
    private entityMetabaseService: EntityMetabaseService,
    private entityService: EntityService,
    private loopbackCrudApiService: LoopbackCrudApiService,
    private entityVisualizationService: EntityVisualizationService,
    private cd: ChangeDetectorRef,
    private jsonataParserService: JsonataParserService
  ) {
    // Constructor intentionally left empty
  }

  ngOnInit() {
    this.subscribeToData();
  }

  ngAfterViewInit() {
    // Scroll to top on initial render
    requestAnimationFrame(() => {
      this.scrollToTop();
      if (this.displayedSection === 'all') {
        setTimeout(() => {
          this.isFirstPanelExpanded = true;
          this.cd.markForCheck();
        }, 100);
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['projectModelId'] && !changes['projectModelId'].isFirstChange()) {
      const newProjectModelId = changes['projectModelId'].currentValue;
      if (newProjectModelId && newProjectModelId !== this.projectModelId) {
        this.projectModelId = newProjectModelId;
        if (this.projectModels?.length) {
          this.selectProjectByModelId();
        }
      }
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  subscribeToData() {
    this.subscriptions = [
      this.subscribeToRouteParameters(),
      this.subscribeToProjectModels()
    ];
  }

  subscribeToRouteParameters() {
    return this.route.paramMap.subscribe((params: ParamMap) => {
      const routeProjectModelId = params.get('projectModelId') ?? undefined;
      if (routeProjectModelId && routeProjectModelId !== this.projectModelId) {
        this.projectModelId = routeProjectModelId;
        this.selectProjectByModelId();
      }
    });
  }

  subscribeToProjectModels() {
    return this.store.select(getProjectModelsWithFields).subscribe((projectModels: ProjectModelWithRelations[] | undefined) => {
      this.projectModels = projectModels;
      if (this.recentGridConfigUpdate) {
        setTimeout(() => {
          this.recentGridConfigUpdate = null;
          this.selectProjectByModelId();
        }, 1000);
        return;
      }

      this.selectProjectByModelId();
    });
  }

  selectProjectByModelId() {
    this.invalidateComponentCache();

    this.selectedProjectModel = this.projectModels?.find(pm => pm.id === this.projectModelId) as ProjectModelWithRelations;
    if (this.selectedProjectModel) {
      this.loadEntityConfigFromMetadata();

      this.activeEntityConfig = {
        id: this.selectedProjectModel.id,
        name: this.selectedProjectModel.dataTableName || this.selectedProjectModel.name,
        type: 'entity',
        title: this.selectedProjectModel.name,
        description: `Entity configuration for ${this.selectedProjectModel.name}`,
        config: this.entityConfig?.config
      };
    } else {
      this.entityConfig = undefined;
    }

    if (this.entityConfig) {
      this.fetchEntityData();
    } else {
      this.isEntityDataLoading = false;
    }

    this.cd.markForCheck();
  }

  fetchEntityData() {
    if (this.entityConfig && this.selectedProjectModel) {
      // set loading status to true
      this.isEntityDataLoading = true;
      this.loopbackCrudApiService.findFromProjectEntity(
        { entityName: this.entityConfig?.name, filter: this.entityConfig?.config?.apiConfig?.getAll?.filter || null }
      ).subscribe(data => {
        if (!data || !Array.isArray(data) || data.length === 0) {
          this.entityData = this.generateExampleData();
          this.isExampleData = true;
        } else {
          this.entityData = data;
          this.isExampleData = false;
        }
        this.updateConfigsForExampleData();
        this.selectedItemDataViewIndex = 0;
        this.isEntityDataLoading = false;
        this.cd.markForCheck();
      }, error => {
        console.error('Error fetching entity data: ', error);
        this.entityData = this.generateExampleData();
        this.isExampleData = true;
        this.updateConfigsForExampleData();
        this.selectedItemDataViewIndex = 0;
        this.isEntityDataLoading = false;
        this.cd.markForCheck();
      });
    }
  }

  /**
   * Generate example data for the current entity when real data is not available
   * @returns Array of example data objects
   */
  private generateExampleData(): any[] { // eslint-disable-line @typescript-eslint/no-explicit-any
    if (!this.selectedProjectModel) {
      return [];
    }

    try {
      const entityMetaConfig = this.entityMetabaseService.generateConfig(this.selectedProjectModel);
      const exampleData = this.entityService.generateExampleData(entityMetaConfig);
      return exampleData.map(item => ({
        ...item,
        isUpdatable: false,
        isDeletable: false
      }));
    } catch (error) {
      console.error('Error generating example data: ', error);
      return [];
    }
  }

  /**
   * Load entity config and form configs from metadata
   */
  private loadEntityConfigFromMetadata(): void {
    this.entityConfig = this.generateEntityConfig(this.selectedProjectModel as ProjectModelWithRelations);
    this. metadataConfig = this.selectedProjectModel?.metadata?.find(item => item.key === 'uiEntityConfig')?.value?.config;

    const applyConfig = (key: string, metadataKey: string, fallbackKey: string) => {
      const value = this.metadataConfig?.[metadataKey] || this.selectedProjectModel?.[fallbackKey];
      if (value) {
        this.entityConfig.config[key] = value;
      }
    };

    applyConfig('listGridConfig', 'listGridConfig', 'gridConfig');
    applyConfig('cardListConfig', 'cardListConfig', 'cardListConfig');
    applyConfig('createItemFormConfig', 'createItemFormConfig', 'createFormConfig');
    applyConfig('updateItemFormConfig', 'updateItemFormConfig', 'updateFormConfig');
    applyConfig('detailViewConfig', 'detailViewConfig', 'detailViewConfig');
  }

  generateEntityConfig(projectModel: ProjectModelWithRelations) {
    const entityMetaConfig = this.entityMetabaseService.generateConfig(projectModel);
    entityMetaConfig.dataFetchedFrom = 'loopback-root-entity-api';
    const entityConfig = this.entityService.generateEntityConfig(entityMetaConfig);
    return entityConfig;
  }

  /**
   * Scroll to top of all scroll containers
   * Optimized to only scroll containers that exist and need scrolling
   */
  private scrollToTop(): void {
    const innerLayout = document.querySelector('.inner-layout') as HTMLElement;
    const contentContainer = document.querySelector('.manage-entity-content-container') as HTMLElement;

    if (innerLayout && innerLayout.scrollTop > 0) {
      innerLayout.scrollTop = 0;
    }

    if (contentContainer && contentContainer.scrollTop > 0) {
      contentContainer.scrollTop = 0;
    }

    if (window.scrollY > 0) {
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
  }

  onCanvasButtonClicked(buttonKey: unknown) {
    console.log('onCanvasButtonClicked ', buttonKey);
  }

  /**
   * Navigate to component browser to configure the component
   */
  onConfigureComponentClick(): void {
    const componentDefinitionId = this.getDisplayComponentDefinitionId();
    if (componentDefinitionId) {
      this.store.dispatch(new Navigate({
        url: `${PATH_DEFINITIONS.COMPONENT_BROWSER.COMPONENT_DEFINITION_DETAIL_PATH}/${componentDefinitionId}`
      }));
    } else {
      this.store.dispatch(new Navigate({
        url: PATH_DEFINITIONS.COMPONENT_BROWSER.COMPONENT_BROWSER_PATH
      }));
    }
  }

  onToolbarButtonClicked(buttonKey: string) {
    const newSection = (!buttonKey || buttonKey === 'allViews') ? 'all' : buttonKey;

    if (this.displayedSection === newSection) {
      return;
    }

    if (this.pendingSection === newSection) {
      return;
    }

    this.pendingSection = newSection;

    if (newSection !== 'all') {
      this.expandAllSections = false;
      this.isFirstPanelExpanded = false;
    } else {
      this.isFirstPanelExpanded = false;
    }

    if (newSection === 'all') {
      this.scrollToTop();
    }

    this.displayedSection = '';
    this.cd.detectChanges();

    setTimeout(() => {
      if (this.pendingSection === newSection) {
        this.displayedSection = newSection;
        this.pendingSection = null;
        this.cd.markForCheck();

        if (newSection === 'all') {
          requestAnimationFrame(() => {
            this.scrollToTop();
            this.isFirstPanelExpanded = true;
            this.cd.markForCheck();
            setTimeout(() => {
              this.scrollToTop();
            }, 300);
          });
        }
      }
    }, 0);
  }

  /**
   * Get button type (primary/default) based on whether the button is currently selected
   * @param buttonKey The button key to check
   * @returns ButtonType.Primary if selected, ButtonType.Default otherwise
   */
  get AlertTypes() {
    return AlertTypes;
  }

  get HeadingType() {
    return HeadingType;
  }

  /**
   * Update configs to disable Edit and Delete actions for example data
   */
  private updateConfigsForExampleData(): void {
    if (!this.entityConfig?.config) {
      return;
    }

    const updateCardButtons = (config: any, disabled: boolean) => { // eslint-disable-line @typescript-eslint/no-explicit-any
      ['editItemActionButton', 'deleteItemActionButton'].forEach(key => {
        if (config[key]) {
          config[key] = { ...config[key], disabled };
        }
      });
    };

    if (this.entityConfig.config.listGridConfig) {
      const gridConfig = { ...this.entityConfig.config.listGridConfig };
      if (this.isExampleData) {
        gridConfig.handleDisableFn = this.disableExampleDataActions.bind(this);
      } else {
        delete gridConfig.handleDisableFn;
      }
      this.entityConfig.config.listGridConfig = gridConfig;
    }

    if (this.entityConfig.config.cardListConfig) {
      const cardConfig = { ...this.entityConfig.config.cardListConfig };
      updateCardButtons(cardConfig, this.isExampleData);
      this.entityConfig.config.cardListConfig = cardConfig;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private disableExampleDataActions(_data: any[], _item: any, action: any): boolean {
    const isEditOrDelete = action?.name === 'edit-item' || action?.name === 'delete-item';
    return isEditOrDelete ? true : (action?.disabled || false);
  }

  getToolbarButtonType(buttonKey: string): ButtonType {
    if (!buttonKey || buttonKey === 'allViews') {
      return this.displayedSection === 'all' ? ButtonType.Primary : ButtonType.Default;
    }
    return this.displayedSection === buttonKey ? ButtonType.Primary : ButtonType.Default;
  }

  onCollapseHeaderActionButtonClicked(event: Event, key: string) {
    event.stopPropagation();
    console.log('onCollapseHeaderActionButtonClicked ', key);
  }

  /**
   * Get the appropriate text for the design button based on displayWithComponent existence
   * @returns Button text - Professional corporate text based on component existence
   */
  getDesignButtonText(): string {
    return this.hasDisplayComponent()
      ? 'Update Visual Component'
      : 'Design Visual Component';
  }

  /**
   * Check if entity has a display component configured (with caching)
   * @returns True if display component exists
   */
  hasDisplayComponent(): boolean {
    this.updateComponentCacheIfNeeded();
    return this.componentCache.hasDisplayComponent;
  }

  /**
   * Get the display component bundle URL (with caching)
   * @returns Bundle URL for the display component
   */
  getDisplayComponentBundleUrl(): string | null {
    this.updateComponentCacheIfNeeded();
    return this.componentCache.bundleUrl;
  }

  /**
   * Get the display component definition ID (with caching)
   * @returns Component definition ID
   */
  getDisplayComponentDefinitionId(): string | null {
    this.updateComponentCacheIfNeeded();
    return this.componentCache.definitionId;
  }

  /**
   * Update component cache if needed (performance optimization)
   */
  private updateComponentCacheIfNeeded(): void {
    const currentProjectModelId = this.selectedProjectModel?.id;

    // Cache is valid if same project model and cache is marked as valid
    if (this.componentCache.isValid && this.componentCache.lastProjectModelId === currentProjectModelId) {
      return;
    }

    // Update cache
    this.componentCache.hasDisplayComponent = this.entityVisualizationService.hasDisplayComponent(this.selectedProjectModel || null);
    this.componentCache.bundleUrl = this.entityVisualizationService.getEntityComponentBundleUrl(this.selectedProjectModel || null);
    this.componentCache.definitionId = this.entityVisualizationService.getComponentDefinitionId(this.selectedProjectModel || null);
    this.componentCache.lastProjectModelId = currentProjectModelId || null;
    this.componentCache.isValid = true;
  }

  /**
   * Invalidate component cache (call when entity config changes)
   */
  private invalidateComponentCache(): void {
    this.componentCache.isValid = false;
    this.componentCache.lastProjectModelId = null;
  }

  /**
   * Handle generate/update custom component with AI button click
   */
  onGenerateCustomComponentWithAIButtonClicked(): void {
    this.router.navigate(['/ai', 'generate-entity-component', this.selectedProjectModel?.id]);
  }


  /**
   * Grid builder configuration
   * @param gridConfig The grid configuration object
   */
  onGridBuilderConfigSaved(gridConfig: GridBuilderConfig) {
    if (!gridConfig || !this.selectedProjectModel?.id) {
      return;
    }

    this.recentGridConfigUpdate = this.selectedProjectModel.dataTableName || this.selectedProjectModel.name;
    if (this.entityConfig && this.entityConfig.config) {
      this.entityConfig = {
        ...this.entityConfig,
        config: {
          ...this.entityConfig.config,
          listGridConfig: gridConfig as any // eslint-disable-line @typescript-eslint/no-explicit-any
        }
      };
    }
    if (this.selectedProjectModel) {
      this.selectedProjectModel.gridConfig = gridConfig;
    }

    this.store.dispatch(new ProjectModelActions.UpdateProjectModelGridConfig({
      projectModelId: this.selectedProjectModel.id,
      gridConfig: gridConfig
    }));
  }

  /**
   * List grid visibility columns changes
   * @param visibilityColumns The updated visibility columns array
   */
  onListGridVisibilityColumnsChanged(visibilityColumns: string[]) {
    if (!visibilityColumns || !this.selectedProjectModel?.id) {
      return;
    }
    this.recentGridConfigUpdate = this.selectedProjectModel.dataTableName || this.selectedProjectModel.name;
    const existingGridConfig = this.selectedProjectModel?.gridConfig || {};
    const mergedGridConfig = {
      ...existingGridConfig,
      visibilityColumns: visibilityColumns,
      lastVisibilityColumnsUpdate: new Date().toISOString()
    };
    this.store.dispatch(new ProjectModelActions.UpdateProjectModelGridConfig({
      projectModelId: this.selectedProjectModel.id,
      gridConfig: mergedGridConfig
    }));
  }

  /**
   * Handle form builder configuration save
   * @param formType The form type (create or update)
   * @param formConfig The form configuration object
   */
  private updateEntityConfigFormConfig(formType: 'create' | 'update', formConfig: CrudFormConfig) {
    if (!formConfig || !this.entityConfig?.config) {
      return;
    }

    if (this.entityConfig && this.entityConfig.config) {
      if (formType === 'create') {
        this.entityConfig.config.createItemFormConfig = formConfig;
      } else if (formType === 'update') {
        this.entityConfig.config.updateItemFormConfig = formConfig;
      }
    }
  }

  /**
   * Handle card builder configuration save
   * @param cardConfig The card configuration object
   */
  onCardBuilderConfigSaved(cardConfig: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
    if (!cardConfig || !this.selectedProjectModel?.id) {
      return;
    }

    this.recentGridConfigUpdate = this.selectedProjectModel.dataTableName || this.selectedProjectModel.name;
    if (this.entityConfig && this.entityConfig.config) {
      this.entityConfig = {
        ...this.entityConfig,
        config: {
          ...this.entityConfig.config,
          cardListConfig: cardConfig
        }
      };
    }
    if (this.selectedProjectModel) {
      this.selectedProjectModel.cardListConfig = cardConfig;
    }

    this.store.dispatch(new ProjectModelActions.UpdateProjectModelCardConfig({
      projectModelId: this.selectedProjectModel.id,
      cardListConfig: cardConfig
    }));
  }

  /**
   * Handle item data builder configuration save
   * @param detailViewConfig The detail view configuration object
   */
  onItemDataBuilderConfigSaved(detailViewConfig: CrudFormConfig) {
    if (!detailViewConfig || !this.selectedProjectModel?.id) {
      return;
    }

    if (this.entityConfig && this.entityConfig.config) {
      this.entityConfig = {
        ...this.entityConfig,
        config: {
          ...this.entityConfig.config,
          detailViewConfig: detailViewConfig
        }
      };

      if (this.activeEntityConfig) {
        this.activeEntityConfig = {
          ...this.activeEntityConfig,
          config: {
            ...this.activeEntityConfig.config,
            detailViewConfig: detailViewConfig
          }
        };
      }
    }
    if (this.selectedProjectModel) {
      this.selectedProjectModel.detailViewConfig = detailViewConfig;
    }

    this.store.dispatch(new ProjectModelActions.UpdateProjectModelDetailViewConfig({
      projectModelId: this.selectedProjectModel.id,
      detailViewConfig: detailViewConfig
    }));

    this.recentGridConfigUpdate = this.selectedProjectModel.dataTableName || this.selectedProjectModel.name;

    this.cd.markForCheck();
  }

  /**
   * Handle form builder configuration save from crud-view
   * @param event The form builder configuration event
   */
  onFormBuilderConfigSaved(event: { formType: 'create' | 'update'; config: CrudFormConfig }) {
    if (!event?.config || !this.selectedProjectModel?.id) {
      return;
    }


    // Update local entity config
    this.updateEntityConfigFormConfig(event.formType, event.config);

    if (event.formType === 'create') {
      this.selectedProjectModel.createFormConfig = event.config;
      this.store.dispatch(new ProjectModelActions.UpdateProjectModelCreateFormConfig({
        projectModelId: this.selectedProjectModel.id,
        createFormConfig: event.config
      }));
    } else if (event.formType === 'update') {
      this.selectedProjectModel.updateFormConfig = event.config;
      this.store.dispatch(new ProjectModelActions.UpdateProjectModelUpdateFormConfig({
        projectModelId: this.selectedProjectModel.id,
        updateFormConfig: event.config
      }));
    }

  }


  /**
   * Get default form configuration
   * @param formType The form type (create or update)
   * @returns Default form configuration
   */
  private getDefaultFormConfig(formType: 'create' | 'update'): CrudFormConfig {
    return {
      items: [],
      formLayout: 'vertical' as any, // eslint-disable-line @typescript-eslint/no-explicit-any
      submitButton: {
        visible: true,
        text: formType === 'create' ? 'Create' : 'Update',
        type: 'primary' as any, // eslint-disable-line @typescript-eslint/no-explicit-any
        size: 'default' as any, // eslint-disable-line @typescript-eslint/no-explicit-any
        block: false
      }
    };
  }

  /**
   * Get the currently selected item for detail view
   * @returns The selected item from entityData, or undefined if no data available
   */
  getSelectedItemForDetailView(): any { // eslint-disable-line @typescript-eslint/no-explicit-any
    if (!this.entityData?.length) {
      return undefined;
    }
    const validIndex = Math.max(0, Math.min(this.selectedItemDataViewIndex, this.entityData.length - 1));
    return this.entityData[validIndex];
  }

  /**
   * Handle item selection change in detail view
   * @param index The index of the selected item
   */
  onItemDataViewSelectionChange(index: number): void {
    if (this.entityData && index >= 0 && index < this.entityData.length) {
      this.selectedItemDataViewIndex = index;
      this.cd.markForCheck();
    }
  }

  /**
   * Get options array for item selector dropdown
   * @returns Array of options with label and value for each item
   */
  getItemSelectorOptions(): Array<{ label: string; value: number }> {
    if (!this.entityData?.length) {
      return [];
    }
    const titleFieldName = this.entityConfig?.config?.titleFieldName || 'id';
    return this.entityData.map((item, index) => ({
      label: String(item[titleFieldName] || item.name || item.title || `Item ${index + 1}`),
      value: index
    }));
  }

  /**
   * Handle grid row item selection from Grid View
   * When a row is clicked in the grid, this method:
   * 1. Finds the clicked item in entityData
   * 2. Updates selectedItemDataViewIndex
   * 3. Expands the Item Data View section
   * 4. Scrolls to the Item Data View section
   * @param item The selected item from the grid row click
   */
  onGridRowItemSelected(item: any): void { // eslint-disable-line @typescript-eslint/no-explicit-any
    const itemIndex = this.findItemIndex(item);
    if (itemIndex < 0) {
      return;
    }

    this.selectedItemDataViewIndex = itemIndex;
    this.displayedSection = 'itemDataView';
    this.cd.markForCheck();
    setTimeout(() => this.scrollToItemDataView(), 100);
  }

  /**
   * Handle grid row item selection from Item Data View's internal grid
   * This is called when a row is clicked in the grid that appears after clicking breadcrumb
   * in Item Data View. This method:
   * 1. Finds the clicked item in entityData
   * 2. Updates selectedItemDataViewIndex
   * 3. Changes viewMode back to Detail if it's currently List
   * 4. Triggers change detection to update the detail view
   * @param item The selected item from the grid row click
   */
  onItemDataViewGridRowItemSelected(item: any): void { // eslint-disable-line @typescript-eslint/no-explicit-any
    const itemIndex = this.findItemIndex(item);
    if (itemIndex < 0) {
      return;
    }

    this.selectedItemDataViewIndex = itemIndex;
    if (this.itemDataViewCrudView?.viewMode === ViewMode.List) {
      this.itemDataViewCrudView.setViewMode(ViewMode.Detail);
    }
    this.cd.markForCheck();
  }

  /**
   * Find the index of an item in entityData
   * @param item The item to find
   * @returns The index of the item, or -1 if not found
   */
  private findItemIndex(item: any): number { // eslint-disable-line @typescript-eslint/no-explicit-any
    if (!item || !this.entityData?.length) {
      return -1;
    }

    const idFieldName = this.activeEntityConfig?.config?.idFieldName || 'id';
    const idFields = new Set([idFieldName, 'id', '_id']);

    return this.entityData.findIndex(dataItem => {
      if (dataItem === item) {
        return true;
      }

      for (const field of idFields) {
        const dataItemId = dataItem[field];
        const itemId = item[field];
        if (dataItemId != null && itemId != null && String(dataItemId) === String(itemId)) {
          return true;
        }
      }

      return false;
    });
  }

  /**
   * Scroll to Item Data View section smoothly
   */
  private scrollToItemDataView(): void {
    const allPanels = document.querySelectorAll('.manage-entity-collapse-panel');
    const itemDataViewPanel = Array.from(allPanels).find(panel => {
      const header = panel.querySelector('.collapse-header-container');
      return header?.textContent?.includes('Item Data View');
    });

    const targetElement = itemDataViewPanel || document.querySelector('.manage-entity-collapse');
    if (targetElement) {
      targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }


}
